/**
 * Pure WCAG 2.x contrast math over normalized sRGB channel objects
 * ({r, g, b, a?} — r/g/b in 0..255, a in 0..1, the shape produced by
 * shared/color-utils.js parseCssColor). This module never parses CSS text:
 * callers normalize first, so the repo keeps exactly one color parser.
 * Zero imports by design — usable from audit checks, UI panels, the iframe
 * bridge's parent-side consumers, and plain-node tests alike.
 */

const clampChannel = (value) => Math.min(255, Math.max(0, Number(value) || 0));
const alphaOf = (color) => {
  const a = Number(color?.a);
  return Number.isFinite(a) ? Math.min(1, Math.max(0, a)) : 1;
};

function linearizeChannel(value) {
  const channel = clampChannel(value) / 255;
  return channel <= 0.04045 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4);
}

/** WCAG relative luminance of an (assumed opaque) sRGB color. */
export function relativeLuminance(rgb) {
  if (!rgb) return Number.NaN;
  return (
    0.2126 * linearizeChannel(rgb.r) +
    0.7152 * linearizeChannel(rgb.g) +
    0.0722 * linearizeChannel(rgb.b)
  );
}

/** WCAG contrast ratio between two opaque colors. NaN when either is missing. */
export function contrastRatio(rgbA, rgbB) {
  const la = relativeLuminance(rgbA);
  const lb = relativeLuminance(rgbB);
  if (!Number.isFinite(la) || !Number.isFinite(lb)) return Number.NaN;
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** Source-over composite of a (possibly translucent) color onto an opaque one. */
export function compositeOver(fgRgba, bgRgb) {
  if (!fgRgba || !bgRgb) return null;
  const a = alphaOf(fgRgba);
  const mix = (over, under) => clampChannel(over) * a + clampChannel(under) * (1 - a);
  return { r: mix(fgRgba.r, bgRgb.r), g: mix(fgRgba.g, bgRgb.g), b: mix(fgRgba.b, bgRgb.b), a: 1 };
}

/**
 * WCAG "large text": at least 18pt (24px), or at least 14pt bold (18.66px at
 * weight ≥ 700). Unknown size/weight is conservatively NOT large.
 */
export function isLargeText({ fontPx, fontWeight } = {}) {
  const px = Number(fontPx);
  if (!Number.isFinite(px)) return false;
  if (px >= 24) return true;
  const weight = Number(fontWeight);
  return px >= 18.66 && Number.isFinite(weight) && weight >= 700;
}

/**
 * Assess fg over bg. Translucent foregrounds are composited over the
 * background before measuring — a 50%-alpha black on white is judged as the
 * grey the reader actually sees. `aa`/`aaa` are the verdicts for the ACTUAL
 * text size: a 3:1 result only passes when the text qualifies as large.
 */
export function assessContrast(fgRgba, bgRgb, { fontPx, fontWeight } = {}) {
  if (!fgRgba || !bgRgb) return null;
  const effectiveFg = alphaOf(fgRgba) < 0.999 ? compositeOver(fgRgba, bgRgb) : fgRgba;
  const ratio = contrastRatio(effectiveFg, bgRgb);
  if (!Number.isFinite(ratio)) return null;
  const large = isLargeText({ fontPx, fontWeight });
  return {
    ratio,
    label: `${ratio.toFixed(1)}:1`,
    large,
    aa: ratio >= (large ? 3 : 4.5),
    aaa: ratio >= (large ? 4.5 : 7),
    wouldPassLarge: ratio >= 3
  };
}

/**
 * Badge presentation for an assessment: `state` is 'pass' | 'fail' | null.
 * A failing ratio that would pass as large text carries that fact in `note`
 * (rendered as a tooltip), never as a pass.
 */
export function formatBadge(assessment) {
  if (!assessment) return null;
  if (assessment.aa) {
    const tier = assessment.aaa ? 'AAA' : 'AA';
    return {
      text: `${assessment.label} ${tier}${assessment.large ? ' (large text)' : ''}`,
      state: 'pass',
      note: ''
    };
  }
  return {
    text: `${assessment.label} Fail`,
    state: 'fail',
    note: assessment.wouldPassLarge && !assessment.large ? 'Would pass as large text (24px+)' : ''
  };
}
